import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { BRAND } from '../data/brand';

const TEAM_MEMBERS = [
  {
    name: 'Marcus Vane',
    role: 'Founder',
    bio: 'Former luxury brand strategist who left the corporate world to build something truly private. Marcus set the vision for what Secret 13 would become.',
    image: 'https://images.unsplash.com/photo-1701463387028-3947648f1337?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBkYXJrJTIwcG9ydHJhaXQlMjBwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdHxlbnwwfHx8fDE3NzQ5MTY4MTJ8MA&ixlib=rb-4.1.0&q=85',
  },
  {
    name: 'Elijah Osei',
    role: 'Curator',
    bio: 'Came from the culture, built for the connoisseurs. Elijah\'s taste was sharpened on the streets before it was refined in the lab. Nothing enters the collection without his sign-off.',
    image: 'https://images.pexels.com/photos/10660651/pexels-photo-10660651.jpeg',
    darken: true,
    zoom: true,
    objectPos: 'object-[center_20%]',
  },
  {
    name: 'Lena Morel',
    role: 'Head of Experience',
    bio: 'A sensory design specialist who shapes how each Secret feels, from first impression to lasting memory. Every detail is intentional.',
    image: 'https://images.pexels.com/photos/4755024/pexels-photo-4755024.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
  {
    name: 'Theo Ashford',
    role: 'Quality Director',
    bio: 'Decade-long background in botanical science and product testing. Theo ensures every batch meets the standard before it carries the name.',
    image: 'https://images.pexels.com/photos/10657877/pexels-photo-10657877.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
  {
    name: 'Ines Nakamura',
    role: 'Creative Director',
    bio: 'Shapes the visual world of Secret 13 — from packaging to digital presence. Believes luxury is in what you choose not to show.',
    image: 'https://images.unsplash.com/photo-1642290460481-76a5f2e18c3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBkYXJrJTIwcG9ydHJhaXQlMjBwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdHxlbnwwfHx8fDE3NzQ5MTY4MTJ8MA&ixlib=rb-4.1.0&q=85',
  },
];

const VALUES = [
  {
    number: '01',
    title: 'Obsessive Curation',
    description: 'We reject more than we accept. Only what meets our uncompromising standard makes it into the collection.',
  },
  {
    number: '02',
    title: 'Radical Privacy',
    description: 'Your identity, your choices, your moments — they belong to you. We built every system around this belief.',
  },
  {
    number: '03',
    title: 'Experience First',
    description: 'We don\'t sell products. We design experiences. Every Secret is crafted for a specific moment in your life.',
  },
];

const AboutUsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4"
          >
            The People
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-zinc-50 mb-6"
            data-testid="about-us-heading"
          >
            Behind Every Secret
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-sans text-sm text-zinc-500 max-w-lg mx-auto leading-relaxed"
          >
            A small, intentional team united by one conviction — the finest things
            are built quietly, for the few who notice.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="w-12 h-px bg-zinc-700 mx-auto mt-10"
          />
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 md:gap-10">
            {TEAM_MEMBERS.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.15 }}
                data-testid={`team-member-${index}`}
              >
                <div className="group">
                  {/* Photo */}
                  <div className="relative aspect-[3/4] bg-[#0A0A0A] border border-white/5 overflow-hidden mb-5 group-hover:border-white/15 transition-all duration-500">
                    <img
                      src={member.image}
                      alt={member.name}
                      className={`w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ${member.darken ? 'brightness-[0.4]' : ''}`}
                      style={member.zoom ? { transform: 'scale(1.5)', transformOrigin: 'center 1%' } : {}}
                    />
                    <div className="absolute inset-0 bg-amber-500/0 group-hover:bg-amber-500/10 transition-all duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 via-transparent to-transparent" />
                  </div>

                  {/* Info */}
                  <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-zinc-600 mb-1">
                    {member.role}
                  </p>
                  <h3 className="font-serif text-xl text-zinc-100 mb-2">
                    {member.name}
                  </h3>
                  <p className="font-sans text-xs text-zinc-500 leading-relaxed">
                    {member.bio}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-6">
              Our Mission
            </p>
            <div className="w-px h-12 bg-gradient-to-b from-transparent via-zinc-700 to-transparent mx-auto mb-10" />
            <p className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-zinc-300 leading-relaxed mb-4">
              To create a space where quality is quiet,
            </p>
            <p className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-zinc-300 leading-relaxed">
              privacy is sacred, and every experience is personal.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4">
              What Drives Us
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl font-light text-zinc-50">
              Core Principles
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {VALUES.map((value, index) => (
              <motion.div
                key={value.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.15 }}
                data-testid={`value-${index}`}
              >
                <p className="font-sans text-xs tracking-[0.2em] text-zinc-600 mb-4">
                  {value.number}
                </p>
                <h3 className="font-serif text-xl text-zinc-100 mb-3">
                  {value.title}
                </h3>
                <p className="font-sans text-sm text-zinc-500 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing Quote */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="w-px h-16 bg-gradient-to-b from-transparent via-zinc-700 to-transparent mx-auto mb-12" />
            <p className="font-serif text-2xl sm:text-3xl font-light text-zinc-300 leading-relaxed mb-4">
              "We didn't build Secret 13 to be the biggest.
            </p>
            <p className="font-serif text-2xl sm:text-3xl font-light text-zinc-300 leading-relaxed mb-12">
              We built it to be the one you keep coming back to."
            </p>
            <div className="w-12 h-px bg-zinc-700 mx-auto mb-8" />
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-600">
              The Secret 13 Team
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 lg:px-24 border-t border-white/5">
        <div className="max-w-xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="font-sans text-sm text-zinc-500 mb-6">
              Now that you know us — discover what we've made for you.
            </p>
            <button
              data-testid="about-us-cta"
              onClick={() => navigate('/collection')}
              className="bg-white text-black px-10 py-4 font-sans text-xs font-medium tracking-[0.2em] uppercase hover:bg-zinc-200 transition-all duration-300"
            >
              View Collection
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-xl text-zinc-50">Secret 13</p>
          <p className="font-sans text-xs text-zinc-600">
            {BRAND.legal.copyright} {BRAND.legal.membersOnly}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AboutUsPage;
